import { Tooltip } from './Tooltip'

// ─────────────────────────────────────────────────────────────────────────────
// StatusChip — domain-agnostic colored status pill.
//
// Presentational only. Caller supplies the visual tokens (label, bg, text) and
// the chip renders them — no knowledge of which statuses exist or what they
// mean. Each feature owns its own status→style mapping (e.g. the load-status
// palette lives in `src/constants/load-status.ts`).
//
// When `reason` is provided, the chip is wrapped in a tooltip so hovering
// reveals *why* the status was assigned (typically an AI-generated explanation).
// ─────────────────────────────────────────────────────────────────────────────

export interface StatusChipProps {
    /** User-facing label rendered inside the chip. */
    label: string
    /** Tailwind background classes (light + dark) — caller picks the palette. */
    bg: string
    /** Tailwind text-color classes (light + dark) — caller picks the palette. */
    text: string
    /** Optional explanation rendered as a hover tooltip. */
    reason?: string | null
    /** Size — sm (default) matches the dense LoadRow Status column. */
    size?: 'sm' | 'md'
    className?: string
}

const sizeClasses: Record<NonNullable<StatusChipProps['size']>, string> = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-0.5 text-xs',
}

export const StatusChip = ({ label, bg, text, reason, size = 'sm', className = '' }: StatusChipProps) => {
    // Render the pill directly rather than composing on top of <Badge>. Badge's
    // soft/default palette would paint slate underneath and Tailwind JIT can't
    // !-override dynamic class strings — it only scans literal source. Caller-
    // supplied class strings are expected to be static module-level literals,
    // so emitting them straight onto a <span> keeps the palette intact and JIT
    // happy. Border-color is implicit (none); shape + size tokens stay aligned
    // with Badge.
    const chip = <span className={`inline-flex items-center gap-1 rounded-full font-medium ${bg} ${text} ${sizeClasses[size]} ${className}`}>{label}</span>

    if (!reason) return chip
    return (
        <Tooltip title={reason} placement="top">
            {chip}
        </Tooltip>
    )
}
